import * as Y from 'yjs';
import { buildYDoc, readYDoc, ydocMaps, LOCAL_ORIGIN } from './schema';
import type { CanvasDoc, Viewport } from '../types';
import { attachSync, type SyncOpts, type SyncFactories, type SyncHandle, type SyncStatus, type Awareness } from './provider';

export interface BindOpts {
  /** When present, run in SYNC mode: start an empty Y.Doc, attach a Hocuspocus
   *  provider + y-indexeddb (server/local fill it). When absent, LOCAL mode:
   *  buildYDoc from `doc`. */
  sync?: SyncOpts;
  /** Test injection for the provider/indexeddb factories. */
  factories?: SyncFactories;
}

export interface YBinding {
  ydoc: Y.Doc;
  undoManager: Y.UndoManager;
  /** Read the current doc, injecting the given (local) viewport. */
  read: (viewport: Viewport) => CanvasDoc;
  /** Subscribe to post-transaction changes (local, remote, undo, redo). */
  onChange: (cb: () => void) => void;
  /** Force an undo-step boundary so the next change starts a new step. */
  commit: () => void;
  /** Resolves once initial sync settles (immediately in local mode). */
  whenSynced: Promise<void>;
  /** Yjs Awareness for presence; `null` in LOCAL mode (single-user). */
  awareness: Awareness | null;
  /** Subscribe to connection-status changes. Returns an unsubscribe fn. In
   *  LOCAL mode there is no provider, so this is a no-op. */
  onStatus: (cb: (s: SyncStatus) => void) => () => void;
  dispose: () => void;
}

/** Build a Y.Doc from `doc` and wire an UndoManager + change observer. Pure of
 *  zustand — the caller supplies the viewport on read and reacts to onChange.
 *
 *  When `opts.sync` is present (SYNC mode), starts from an EMPTY Y.Doc and
 *  attaches a Hocuspocus provider + y-indexeddb; server/indexeddb fill it.
 *  When absent (LOCAL mode, default), buildYDoc from `doc` as before. */
export function bindYDoc(doc: CanvasDoc, opts?: BindOpts): YBinding {
  const ydoc = opts?.sync ? new Y.Doc() : buildYDoc(doc);
  const sync: SyncHandle | null = opts?.sync
    ? attachSync(ydoc, opts.sync, opts.factories)
    : null;

  const m = ydocMaps(ydoc);
  const tracked = [m.frames, m.comments, m.themes, m.tours, m.scalars];
  const undoManager = new Y.UndoManager(tracked, {
    trackedOrigins: new Set([LOCAL_ORIGIN]),
    captureTimeout: 1_000_000, // never auto-split; we split explicitly via commit()
  });

  const cbs = new Set<() => void>();
  const handler = () => { for (const cb of cbs) cb(); };
  ydoc.on('afterTransaction', handler);

  return {
    ydoc,
    undoManager,
    whenSynced: sync ? sync.whenSynced : Promise.resolve(),
    awareness: sync ? sync.awareness : null,
    onStatus: sync ? sync.onStatus : () => () => {},
    read: (viewport) => readYDoc(ydoc, viewport),
    onChange: (cb) => { cbs.add(cb); },
    commit: () => undoManager.stopCapturing(),
    dispose: () => {
      ydoc.off('afterTransaction', handler);
      cbs.clear();
      undoManager.destroy();
      sync?.destroy();
      ydoc.destroy();
    },
  };
}
